import {
  useEffect, useRef, useState,
  type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent,
} from 'react'
import { HOLD_MS, dragIntent, moveRelative, type DropSide } from './reorder'
import { isTypingTarget } from './typing'

/** Long enough to eat the click a pick-up or a drop ends in. */
const SWALLOW_MS = 400

/** A row of things carried along its own axis scrolls when the finger is
 *  this close to its edge: `MIN_STEP` pixels a frame at the margin's inner
 *  edge, `MAX_STEP` at (and past) the edge itself. */
const EDGE_PX = 40
const MIN_STEP = 2
const MAX_STEP = 10

export interface ReorderState {
  /** The row picked up. */
  id: string
  /** Rows only reorder among their own group (siblings, or a Lists section). */
  group: string
  /** The row under the pointer, in the same group, or null. */
  over: string | null
  /** `axis: 'x'` only: which half of `over` the finger is on, which is the
   *  side the dragged row will land on. Null otherwise. */
  side: DropSide | null
}

/** `'y'`: rows in a column. `'x'`: chips in one row that scrolls sideways.
 *  `'wrap'`: chips that wrap onto more lines and do not scroll (the Lists
 *  tab since 6 Oct) — carried in both directions, aimed by line then by x. */
export type ReorderAxis = 'x' | 'y' | 'wrap'

interface Gesture {
  id: string; group: string; axis: ReorderAxis
  x: number; y: number; pointerId: number; pointerType: string
  el: HTMLElement
  held: boolean; dragging: boolean; moved: boolean
  over: string | null
  side: DropSide | null
  timer: ReturnType<typeof setTimeout> | null
  /** `axis: 'x'` bookkeeping: where the finger is now, the scrolling row, and
   *  what to put back when the gesture ends. */
  lastX: number
  lastY: number
  scroller: HTMLElement | null
  scroll0: number
  base: number
  minAt: number
  maxAt: number
  touchAction: string
  raf: number | null
}

/**
 * Drag to reorder, with pointer events: hold a finger still for 450ms to pick
 * a row up and then drag it; with a mouse, a vertical drag picks it up at
 * once (`dragIntent` in `reorder.ts`). HTML5 `draggable` fires nothing at all
 * from a touch, which is why the Lists index left it in WP 4.2 and why the
 * items inside a list could not be reordered on the phone (UI-audit a2-11).
 * This is the index's own long-press drag, lifted out so both use one copy.
 *
 * Spread `bind(id, group)` onto the element that moves. Elements may nest (a
 * task's sub-tasks sit inside its own element): a gesture belongs to the
 * innermost one, and only rows of the same group are drop targets.
 *
 * Two things the index's copy did not do. While a row is picked up a
 * non-passive `touchmove` listener cancels the page scroll — a row's
 * `touch-action: pan-y` would otherwise let the browser take the vertical
 * drag, scroll instead, and cancel the pointer stream. And moves are stopped
 * in the capture phase once a row is up, so `Row`'s own swipe underneath does
 * not follow the finger too.
 *
 * `onHold` fires for a pick-up let go without moving — the index opens a
 * list's menu on it. Anything inside `exclude` (by default a row's trailing
 * controls) or a field being typed in never starts a gesture.
 */
export function usePointerReorder({ onDrop, onHold, exclude = '.u-row__trail' }: {
  onDrop: (dragId: string, overId: string, group: string, side: DropSide | null) => void
  onHold?: (id: string) => void
  exclude?: string
}) {
  const [state, setState] = useState<ReorderState | null>(null)
  const g = useRef<Gesture | null>(null)
  const swallow = useRef(false)

  // While a row is up, the page does not scroll under it. Registered for the
  // life of the hook rather than per gesture so it is in place before the
  // first touchmove a pick-up could see; it does nothing unless a row is up.
  useEffect(() => {
    const block = (e: TouchEvent) => { if (g.current?.dragging && e.cancelable) e.preventDefault() }
    document.addEventListener('touchmove', block, { passive: false })
    return () => document.removeEventListener('touchmove', block)
  }, [])

  function swallowNextClick() {
    swallow.current = true
    setTimeout(() => { swallow.current = false }, SWALLOW_MS)
  }

  function end() {
    const s = g.current
    if (s?.timer != null) clearTimeout(s.timer)
    if (s?.raf != null) cancelAnimationFrame(s.raf)
    if (s?.scroller) s.scroller.style.touchAction = s.touchAction
    if (s?.axis === 'x' || s?.axis === 'wrap') s.el.style.transform = ''
    g.current = null
    setState(null)
  }

  function pickUp(s: Gesture) {
    s.dragging = true
    swallowNextClick()
    try { s.el.setPointerCapture(s.pointerId) } catch { /* the pointer is already gone */ }
    if (s.axis === 'x') {
      const sc = s.el.parentElement
      s.scroller = sc
      if (sc) {
        s.scroll0 = sc.scrollLeft
        // Where the chip sits in the row's own coordinates, so its lifted
        // position can be kept inside the row: a chip carried past the end
        // would widen the row's scroll range for the length of the drag, and
        // the drop would snap it back. `maxAt` is read here, before the first
        // transform: afterwards `scrollWidth` includes the chip's own overflow
        // and a clamp against it would clamp against itself (a first version
        // did, and the row grew to 1091px under a 655px set of chips).
        s.base = s.el.getBoundingClientRect().left - sc.getBoundingClientRect().left + sc.scrollLeft
        // The chip stays between the row's own padding, where the first and
        // last chips start and end, so it is never carried half out of sight.
        const pad = getComputedStyle(sc)
        s.minAt = parseFloat(pad.paddingLeft) || 0
        s.maxAt = Math.max(s.minAt, sc.scrollWidth - s.el.offsetWidth - (parseFloat(pad.paddingRight) || 0))
        // `touch-action` is sampled when a touch starts, so for the finger
        // already down this changes nothing in Chrome — the document
        // `touchmove` listener above is what stops the pan. It is for a
        // browser that re-reads it mid-gesture, and costs nothing otherwise.
        s.touchAction = sc.style.touchAction
        sc.style.touchAction = 'none'
      }
      s.raf = requestAnimationFrame(() => tick(s))
    }
    // `'wrap'` has no scroller: nothing to clamp to and nothing to scroll,
    // and the document `touchmove` listener above already holds the page.
    setState({ id: s.id, group: s.group, over: null, side: null })
  }

  /** The lifted chip: under the finger, kept inside the row's content. */
  function lift(s: Gesture) {
    if (s.axis === 'wrap') {
      s.el.style.transform = `translate(${s.lastX - s.x}px, ${s.lastY - s.y}px)`
      return
    }
    const sc = s.scroller
    if (!sc) return
    const want = s.base + (s.lastX - s.x) + (sc.scrollLeft - s.scroll0)
    const at = Math.min(Math.max(want, s.minAt), s.maxAt)
    s.el.style.transform = `translateX(${at - s.base}px)`
  }

  /** `axis: 'x'` and `'wrap'`: the sibling nearest the finger and the half of
   *  it the finger is on. Null where dropping would change nothing, so a drop
   *  line is only ever drawn where something will move.
   *
   *  Nearest means the line first, then x: how far the finger is above or
   *  below a chip (0 inside its height) outweighs any horizontal distance, so
   *  a finger on the second line never aims at a chip on the first because
   *  that one's centre happens to be closer. In one row every chip has the
   *  same height, the vertical term is equal for all, and this is x alone. */
  function nearestAt(s: Gesture, x: number, y: number): { id: string; side: DropSide } | null {
    const sibs = [...(s.el.parentElement?.children ?? [])]
      .filter((c): c is HTMLElement => c instanceof HTMLElement && c.dataset.reorderGroup === s.group)
    let best: { id: string; side: DropSide } | null = null
    let bestD = Infinity
    for (const c of sibs) {
      const id = c.dataset.reorderId
      if (!id || id === s.id) continue
      const r = c.getBoundingClientRect()
      const mid = r.left + r.width / 2
      const off = y < r.top ? r.top - y : y > r.bottom ? y - r.bottom : 0
      const d = off * 10_000 + Math.abs(x - mid)
      if (d < bestD) { bestD = d; best = { id, side: x < mid ? 'before' : 'after' } }
    }
    if (!best) return null
    const ids = sibs.map(c => c.dataset.reorderId ?? '')
    return moveRelative(ids, s.id, best.id, best.side) ? best : null
  }

  /** Re-aim after the finger moved or the row scrolled under it. */
  function aim(s: Gesture) {
    lift(s)
    const hit = nearestAt(s, s.lastX, s.lastY)
    const over = hit?.id ?? null
    const side = hit?.side ?? null
    if (over !== s.over || side !== s.side) {
      s.over = over
      s.side = side
      setState(prev => (prev ? { ...prev, over, side } : prev))
    }
  }

  /** One frame of edge auto-scroll while a chip is carried near an end. */
  function tick(s: Gesture) {
    if (g.current !== s) return
    s.raf = requestAnimationFrame(() => tick(s))
    const sc = s.scroller
    if (!sc || !s.moved) return
    const r = sc.getBoundingClientRect()
    const left = Math.max(r.left, 0)
    const right = Math.min(r.right, window.innerWidth)
    const step = (into: number) =>
      Math.round(MIN_STEP + (MAX_STEP - MIN_STEP) * Math.min(Math.max(into, 0), EDGE_PX) / EDGE_PX)
    let v = 0
    if (s.lastX < left + EDGE_PX) v = -step(left + EDGE_PX - s.lastX)
    else if (s.lastX > right - EDGE_PX) v = step(s.lastX - (right - EDGE_PX))
    if (!v) return
    const before = sc.scrollLeft
    sc.scrollLeft = before + v
    if (sc.scrollLeft !== before) aim(s)
  }

  function overAt(s: Gesture, x: number, y: number): string | null {
    let el = document.elementFromPoint(x, y)?.closest<HTMLElement>('[data-reorder-group]') ?? null
    while (el && el.dataset.reorderGroup !== s.group) {
      el = el.parentElement?.closest<HTMLElement>('[data-reorder-group]') ?? null
    }
    const id = el?.dataset.reorderId ?? null
    return id && id !== s.id ? id : null
  }

  /**
   * `axis` `'y'` (the default): rows in a column; the drop target is the row
   * under the finger and the dragged row takes its slot.
   *
   * `'x'`: chips in a row that scrolls sideways (the Lists tab). The chips
   * are the element's siblings in its parent, which is the scrolling
   * element. The picked-up chip follows the finger (a `translateX` on it, so
   * nothing reflows), the row scrolls when the finger is within `EDGE_PX` of
   * either end so a chip can be carried past what is on screen, the target
   * is the sibling whose centre is nearest the finger (so the gutter, the far
   * end and a neighbouring non-chip all still answer), and `onDrop` gets the
   * `side` of it the finger was on — "after the last chip" is a thing.
   * Per call to `bind`, not per hook: the Lists tab binds a row of chips and
   * a column of archived rows to the same hook.
   */
  function bind(id: string, group: string, axis: ReorderAxis = 'y') {
    return {
      'data-reorder-id': id,
      'data-reorder-group': group,
      onPointerDown(ev: ReactPointerEvent<HTMLElement>) {
        if (!ev.isPrimary || (ev.pointerType === 'mouse' && ev.button !== 0)) return
        const t = ev.target as HTMLElement
        // The innermost reorderable element owns the gesture.
        if (t.closest('[data-reorder-id]') !== ev.currentTarget) return
        if ((exclude && t.closest(exclude)) || isTypingTarget(t)) return
        end()
        const s: Gesture = {
          id, group, axis, x: ev.clientX, y: ev.clientY, pointerId: ev.pointerId, pointerType: ev.pointerType,
          el: ev.currentTarget, held: false, dragging: false, moved: false, over: null, side: null, timer: null,
          lastX: ev.clientX, lastY: ev.clientY, scroller: null, scroll0: 0, base: 0, minAt: 0, maxAt: 0, touchAction: '', raf: null,
        }
        s.timer = setTimeout(() => {
          if (g.current !== s) return
          s.timer = null
          s.held = true
          pickUp(s)
        }, HOLD_MS)
        g.current = s
      },
      onPointerMoveCapture(ev: ReactPointerEvent<HTMLElement>) {
        const s = g.current
        if (!s || s.id !== id || ev.pointerId !== s.pointerId) return
        if (!s.dragging) {
          const intent = dragIntent(s.pointerType, ev.clientX - s.x, ev.clientY - s.y, s.held)
          if (intent === 'wait') return
          if (intent === 'cancel') { if (s.timer != null) clearTimeout(s.timer); g.current = null; return }
          pickUp(s)
        }
        ev.stopPropagation()
        // A held finger trembles; only a real move makes this a drag rather
        // than a hold (which the index answers with the list's menu).
        if (!s.moved && dragIntent('touch', ev.clientX - s.x, ev.clientY - s.y, true) === 'wait') return
        s.moved = true
        s.lastX = ev.clientX
        s.lastY = ev.clientY
        if (s.axis === 'x' || s.axis === 'wrap') { aim(s); return }
        const over = overAt(s, ev.clientX, ev.clientY)
        if (over !== s.over) {
          s.over = over
          setState(prev => (prev ? { ...prev, over } : prev))
        }
      },
      onPointerUp(ev: ReactPointerEvent<HTMLElement>) {
        const s = g.current
        if (!s || s.id !== id || ev.pointerId !== s.pointerId) return
        try { s.el.releasePointerCapture(s.pointerId) } catch { /* already released */ }
        const { dragging, moved, over, side } = s
        end()
        if (!dragging) return
        swallowNextClick()
        if (!moved) { onHold?.(id); return }
        if (over) onDrop(id, over, group, side)
      },
      onPointerCancel(ev: ReactPointerEvent<HTMLElement>) {
        if (g.current?.id === id && ev.pointerId === g.current.pointerId) end()
      },
      // A held finger otherwise brings up the system's own long-press menu.
      onContextMenu(ev: ReactMouseEvent<HTMLElement>) {
        if (g.current?.held) ev.preventDefault()
      },
      onClickCapture(ev: ReactMouseEvent<HTMLElement>) {
        if (!swallow.current) return
        swallow.current = false
        ev.preventDefault()
        ev.stopPropagation()
      },
    }
  }

  /** The class the moving row and the row under it draw with (`app.css`).
   *  `is-over--after` is the drop line on the right edge, for `axis: 'x'`. */
  function classFor(id: string) {
    const over = state?.over === id && state.id !== id
    return `draggable${state?.id === id ? ' is-dragging' : ''}${over ? ' is-over' : ''}${over && state.side === 'after' ? ' is-over--after' : ''}`
  }

  return { state, bind, classFor }
}
